import { Outlet, createRoute, redirect, useRouter } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useEffect } from "react"

import { accessStore } from "../../access/access-store"
import { isAccessReady } from "../../access/access-state"
import { AccessReadyProvider, useAccessState } from "../../access/use-access"
import { MasterBookScreen } from "../../screens/master/master-book-screen"
import { MasterBreakScreen } from "../../screens/master/master-break-screen"
import { MasterClientsScreen } from "../../screens/master/master-clients-screen"
import { MasterTimeOffScreen } from "../../screens/master/master-time-off-screen"
import { MasterWeekScreen } from "../../screens/master/master-week-screen"
import { MasterDayScreen } from "../../screens/master/master-day-screen"
import { MasterFrame } from "../../screens/master/master-frame"
import { MasterVisitScreen } from "../../screens/master/master-visit-screen"
import { MasterProfileScreen } from "../../screens/master/master-profile-screen"
import { EventsScreen } from "../../screens/notifications/events-screen"
import { AppLoading, ProblemScreen } from "../../shell/states"
import { rootRoute } from "./root-route"

/**
 * Экраны мастера: /my.
 *
 * ПОЧЕМУ ЭТО ОТДЕЛЬНАЯ ОБЛАСТЬ, А НЕ РАЗДЕЛЫ ВНУТРИ ОБЩЕЙ ОБОЛОЧКИ. Для мастера телефон —
 * единственное устройство, и набор экранов у него свой: «Мой день», «Неделя»,
 * «Мои клиенты», «Профиль» (макет «Мастер - П0»). Общая оболочка кабинета строит
 * навигацию из разделов администратора — Журнал, Клиенты, Каталог, Настройки, — и мастеру
 * из них не нужен ни один. Поставить экраны мастера внутрь неё значило бы нарисовать
 * на одном телефоне две навигации сразу.
 *
 * ПРОВЕРКА ДОСТУПА ТА ЖЕ И ПО ТОМУ ЖЕ ПРАВИЛУ. Профиль дожидается beforeLoad — то есть
 * ДО первой отрисовки, — и пока он грузится, показывается каркас, а не отказ (ADR-012).
 * Это та же конструкция, что у защищённой области кабинета; отличается только то, что
 * рисуется вокруг Outlet.
 *
 * У КАЖДОГО ЭКРАНА СВОЙ АДРЕС. Карточка записи — не модальное окно поверх дня, а адрес
 * /my/visit/<id>: ссылка открывается из уведомления, работает кнопка «назад», и возврат
 * к дню не теряет выбранную дату. То же у формы записи: /my/book?at=870 — это «записать
 * на 14:30», и такую ссылку можно открыть повторно.
 */

function asDate(value: unknown): string | undefined {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined
}

function asText(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined
}

/** Минута от начала дня. Испорченное значение отбрасывается: форма откроется без времени. */
function asMinute(value: unknown): number | undefined {
  const parsed = typeof value === "string" ? Number.parseInt(value, 10) : value
  if (typeof parsed !== "number" || !Number.isFinite(parsed)) return undefined
  if (parsed < 0 || parsed > 24 * 60) return undefined
  return Math.round(parsed)
}

export interface MasterDayParams {
  readonly date?: string | undefined
  readonly branch?: string | undefined
}

/** Форма записи знает ещё и время, с которого её открыли из пустого промежутка ленты. */
export interface MasterBookParams extends MasterDayParams {
  readonly at?: number | undefined
}

export interface MasterClientsParams {
  readonly q?: string | undefined
}

function parseDaySearch(raw: Record<string, unknown>): MasterDayParams {
  return { date: asDate(raw["date"]), branch: asText(raw["branch"]) }
}

function parseBookSearch(raw: Record<string, unknown>): MasterBookParams {
  return { ...parseDaySearch(raw), at: asMinute(raw["at"]) }
}

function parseClientsSearch(raw: Record<string, unknown>): MasterClientsParams {
  return { q: asText(raw["q"]) }
}

/**
 * Область мастера. Маршрут без своего пути (только id) не годится: у экранов мастера
 * общий префикс адреса, и он же отделяет их от кабинета администратора.
 */
export const masterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my",
  beforeLoad: async ({ context, location }) => {
    const state = await context.access.ensureLoaded()
    if (state.kind === "anonymous") {
      throw redirect({ to: "/login", search: { next: location.href } })
    }
    if (state.kind === "tenant_required") {
      throw redirect({ to: "/choose-salon", search: { next: location.href } })
    }
  },
  pendingComponent: AppLoading,
  component: MasterLayout,
})

function MasterLayout(): ReactNode {
  const state = useAccessState()
  const router = useRouter()

  // Сессия может кончиться посреди смены: beforeLoad к тому времени давно отработал,
  // поэтому уводит отсюда эффект, а не маршрут.
  useEffect(() => {
    if (state.kind === "anonymous") {
      void router.navigate({ to: "/login", search: { next: router.state.location.href } })
    }
    if (state.kind === "tenant_required") {
      void router.navigate({ to: "/choose-salon", search: {} })
    }
  }, [state.kind, router])

  if (state.kind === "failed") {
    return (
      <ProblemScreen
        problem={state.problem}
        onRetry={() => {
          void accessStore.reload()
        }}
      />
    )
  }

  // Пока идёт переход на вход — каркас загрузки, а не отказ.
  if (!isAccessReady(state)) return <AppLoading />

  // Рамку с вкладками рисует КАЖДЫЙ экран сам: у них разные активные вкладки, и общая
  // рамка здесь заставила бы экраны сообщать её наверх через контекст ради одного слова.
  return (
    <AccessReadyProvider access={state}>
      <Outlet />
    </AccessReadyProvider>
  )
}

export const masterIndexRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/my/day", search: {}, replace: true })
  },
})

export const masterDayRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/day",
  validateSearch: parseDaySearch,
  component: function MasterDayRouteComponent(): ReactNode {
    return <MasterDayScreen search={masterDayRoute.useSearch()} />
  },
})

export const masterVisitRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/visit/$appointmentId",
  validateSearch: parseDaySearch,
  component: function MasterVisitRouteComponent(): ReactNode {
    const { appointmentId } = masterVisitRoute.useParams()
    return <MasterVisitScreen appointmentId={appointmentId} search={masterVisitRoute.useSearch()} />
  },
})

export const masterBookRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/book",
  validateSearch: parseBookSearch,
  component: function MasterBookRouteComponent(): ReactNode {
    return <MasterBookScreen search={masterBookRoute.useSearch()} />
  },
})

/** 5-М5 «Перерыв»: закрыть себе время одной кнопкой (ТЗ 05 §5.7). */
export const masterBreakRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/break",
  validateSearch: parseDaySearch,
  component: function MasterBreakRouteComponent(): ReactNode {
    return <MasterBreakScreen search={masterBreakRoute.useSearch()} />
  },
})

export const masterClientsRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/clients",
  validateSearch: parseClientsSearch,
  component: function MasterClientsRouteComponent(): ReactNode {
    return <MasterClientsScreen search={masterClientsRoute.useSearch()} />
  },
})

/**
 * «Неделя» (5-М2) — /my/week.
 *
 * Дата в адресе, как и у дня: ссылку на неделю мастер пересылает сменщику, а кнопки
 * «‹ ›» просто двигают её на семь дней. Неделя считается от понедельника той даты,
 * которая в адресе, — а не от самой даты: «неделя с 17-го» и «неделя с 15-го» это
 * одна и та же неделя.
 */
export const masterWeekRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/week",
  validateSearch: parseDaySearch,
  component: function MasterWeekRouteComponent(): ReactNode {
    return <MasterWeekScreen search={masterWeekRoute.useSearch()} />
  },
})

/**
 * Уведомления мастера (1-Э1.9, лента мастера) — /my/notifications.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ АДРЕС, А НЕ БЛОК В ПРОФИЛЕ. Карты макетов ставят ленту мастера
 * и профиль (5-М7) на одну вкладку «Профиль», но как они делят экран — не сказано
 * (вопрос дизайнеру). Пятой вкладки у мастера нет и быть не должно: четыре — это то,
 * что помещается под палец на 360 px. Поэтому лента живёт своим адресом, а в профиле
 * стоит строка со счётчиком дел, которая её открывает: вкладка остаётся одна,
 * а лента — полноэкранной, как в макете.
 *
 * Тот же ЭКРАН, что у администратора: адресность считает сервер, а различие в том,
 * куда ведут кнопки (см. EventsScreen, признак master).
 */
export const masterNotificationsRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/notifications",
  validateSearch: parseDaySearch,
  component: () => (
    <MasterFrame active="profile">
      <EventsScreen master />
    </MasterFrame>
  ),
})

/** «Профиль» (5-М7): что мастер видит о себе и о салоне. */
export const masterProfileRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/profile",
  validateSearch: parseDaySearch,
  component: () => (
    <MasterFrame active="profile">
      <MasterProfileScreen />
    </MasterFrame>
  ),
})

/** 5-М6 «Отпроситься»: просьба об отсутствии делом администратору (вариант А). */
export const masterTimeOffRoute = createRoute({
  getParentRoute: () => masterRoute,
  path: "/time-off",
  component: MasterTimeOffScreen,
})

export const masterRoutes = [
  masterIndexRoute,
  masterDayRoute,
  masterVisitRoute,
  masterBookRoute,
  masterBreakRoute,
  masterClientsRoute,
  masterNotificationsRoute,
  masterWeekRoute,
  masterTimeOffRoute,
  masterProfileRoute,
]
